// Carved from d3-kit's wall-renderer (`wall-tile-composer.js`,
// origin/main @ commit 4b90072 / d3-kit v0.8.0) to seed this repo's own
// published `d3-assets.js` bundle. Transformations applied at carve time:
// the dd-path brush path is stripped (d3-assets never renders ribbon/
// dd-path brushes — those stay in d3-kit), and the wall vocabulary is
// renamed to tiles (`drawWalls`→`drawTileBrush`, `WallBrush`→`TileBrush`,
// `WallTile`→`Tile`, `wallTileUrl`→`tileUrl`,
// `WALL_TEXTURE_PIXELS_PER_CELL`→`TILE_TEXTURE_PIXELS_PER_CELL`). Everything
// else is byte-for-byte d3-kit's renderer, so a future re-sync can diff
// against that source. Re-carve from current d3-kit rather than hand-porting.
//
// Composes wall polylines onto a canvas using a brush manifest.
//
// The user's data is a list of polylines of grid intersections. We render
// the whole list together so that vertices shared by multiple walls (a
// hallway meeting a room wall, for example) get the right vertex piece
// instead of two stacked Endings.
//
// Three passes:
//
//   1. Straight tiles — packed greedy longest-fit-first along each
//      polyline's unit segments across the available Straight piece
//      sizes (3×1, 2×1, 1×1) so long runs read as varied stone rather
//      than a single repeated brick.
//
//   2. Build a global vertex incidence map: for every grid point that
//      any polyline visits, collect the cardinal directions in which a
//      wall extends from that point (across ALL polylines).
//
//   3. Vertex tiles — for each point in the incidence map, pick the
//      tile based on the set of incident directions:
//        1 dir              → Ending (cap rotated toward "no wall" side)
//        2 dirs collinear   → skip (segment tiles cover it)
//        2 dirs perpendic.  → Corner
//        3 dirs             → Joint (T-junction; missing dir = empty side)
//        4 dirs             → Connector (cross)
//
// A wall can run between any two grid intersections. Axis and 45° segments
// follow the passes above; any other segment (e.g. (0,0)→(3,1)) is an *angled
// run*, drawn by drawAngledRun at its true angle — packed straights stretched
// to its √(dx²+dy²) length — and joined to the walls it meets by geometry
// (mitered corners, merged T/crossings; see "Joins at angled runs").
//
// Tile orientation conventions (canonical = angle 0):
//   Straight: runs east (wall material centered on the band, length
//             increases along +x).
//   Corner_A: outside corner with walls exiting east + south (the "ES"
//             shape); other bends are 90° rotations.
//   Ending:   cap accent at tile-local x≈100 (left edge of the wall
//             material rectangle, at tile center); wall material extends
//             east from the cap.
//   Joint:    T with walls extending E + W + S; the empty arm points
//             north. Other Ts are 90° rotations.
//   Connector: cross — walls in all four directions; no rotation.

import { TILE_TEXTURE_PIXELS_PER_CELL, tileUrl } from './tile-brushes.js';

/** @typedef {import('./tile-brushes.js').TileBrush} TileBrush */
/** @typedef {import('./tile-brushes.js').Tile} Tile */
/** @typedef {{x:number,y:number}} Pt */

/**
 * @param {Pt} a
 * @param {Pt} b
 * @param {Pt} c
 */
function isCollinear(a, b, c) {
    return (b.x - a.x) * (c.y - b.y) === (b.y - a.y) * (c.x - b.x);
}

/**
 * Drop interior points whose neighbors are collinear, so every interior
 * point in the result is a true corner.
 * @param {Pt[]} points
 * @returns {Pt[]}
 */
function simplifyPolyline(points) {
    if (points.length <= 2) return points;
    const out = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
        if (!isCollinear(points[i - 1], points[i], points[i + 1])) out.push(points[i]);
    }
    out.push(points[points.length - 1]);
    return out;
}

/**
 * Merge wall polylines that share a brush AND meet at a degree-2
 * vertex (a vertex where exactly two walls' endpoints coincide and
 * no other wall touches that vertex) into single longer polylines.
 *
 * Why: the global straight/bridge/connector z-order works
 * within a single polyline; without merging, two separate walls
 * that visually touch end-to-end render as two independent units
 * with no continuity at the join.
 *
 * Vertices of degree ≥ 3 (T-junctions, crosses) are NOT merged
 * across — those are handled by the existing vertex-incidence pass
 * for axis brushes, which composes Joint / Connector tiles based
 * on global incidence across all walls.
 *
 * @param {Array<{id:string,brushId:string,points:Pt[]}>} walls
 * @returns {Array<{id:string,brushId:string,points:Pt[]}>}
 */
function mergeWallChains(walls) {
    if (walls.length <= 1) return walls;

    // Bucket walls by brushId — chains only merge within the same brush.
    /** @type {Map<string, Array<{id:string,brushId:string,points:Pt[]}>>} */
    const byBrush = new Map();
    for (const w of walls) {
        const arr = byBrush.get(w.brushId);
        if (arr) arr.push(w);
        else byBrush.set(w.brushId, [w]);
    }

    /** @type {Array<{id:string,brushId:string,points:Pt[]}>} */
    const result = [];
    for (const group of byBrush.values()) {
        for (const merged of mergeChainsInBrush(group)) result.push(merged);
    }
    return result;
}

/**
 * Walk same-brush walls finding chains that share degree-2 endpoint
 * vertices, and concatenate each chain into a single polyline.
 * @param {Array<{id:string,brushId:string,points:Pt[]}>} walls
 */
function mergeChainsInBrush(walls) {
    if (walls.length <= 1) return walls;

    // For each unique endpoint vertex, list the walls that touch it
    // and which side (start or end of the polyline).
    /** @type {Map<string, Array<{wi:number, side:0|1}>>} */
    const endpoints = new Map();
    for (let i = 0; i < walls.length; i++) {
        const pts = walls[i].points;
        if (pts.length < 2) continue;
        const sk = `${pts[0].x},${pts[0].y}`;
        const ek = `${pts[pts.length - 1].x},${pts[pts.length - 1].y}`;
        const sArr = endpoints.get(sk);
        if (sArr) sArr.push({ wi: i, side: 0 });
        else endpoints.set(sk, [{ wi: i, side: 0 }]);
        const eArr = endpoints.get(ek);
        if (eArr) eArr.push({ wi: i, side: 1 });
        else endpoints.set(ek, [{ wi: i, side: 1 }]);
    }

    // Per-wall neighbor lookups: neighbor[i][side] gives the wall on
    // the other side of wall i's `side` endpoint, IFF that vertex has
    // exactly degree 2 (only those two walls touching). Higher-degree
    // vertices leave neighbor null on both sides — chains never cross
    // T-junctions.
    /** @type {Array<Array<{wi:number, side:0|1} | null>>} */
    const neighbor = walls.map(() => [null, null]);
    for (const entries of endpoints.values()) {
        if (entries.length !== 2) continue;
        const [a, b] = entries;
        neighbor[a.wi][a.side] = { wi: b.wi, side: b.side };
        neighbor[b.wi][b.side] = { wi: a.wi, side: a.side };
    }

    // Walk chains. For each unvisited wall, walk backward to the chain
    // head (the open end of the chain or the entry point of a closed
    // loop), then walk forward concatenating points.
    /** @type {Set<number>} */
    const visited = new Set();
    /** @type {Array<{id:string,brushId:string,points:Pt[]}>} */
    const merged = [];

    for (let i = 0; i < walls.length; i++) {
        if (visited.has(i)) continue;
        if (walls[i].points.length < 2) {
            visited.add(i);
            merged.push(walls[i]);
            continue;
        }

        // Find chain head: walk backward from i until either a free
        // endpoint, a visited wall, or back to i (closed loop).
        let head;
        let headEnter; // side we'd enter `head` from to walk forward through the chain
        {
            const seen = new Set([i]);
            let cur = i;
            let curStartSide = /** @type {0|1} */ (0); // 0 means head is currently at i's "start"
            while (true) {
                const back = neighbor[cur][curStartSide];
                if (!back) break;
                if (seen.has(back.wi)) break; // closed loop — stop walking back so we don't loop forever
                seen.add(back.wi);
                // Coming back from `cur`, we land on `back.wi` at side `back.side`.
                // The OTHER side of `back.wi` is the new chain-head's "start side".
                cur = back.wi;
                curStartSide = /** @type {0|1} */ (back.side === 0 ? 1 : 0);
            }
            head = cur;
            headEnter = curStartSide;
        }

        // Walk forward from head, accumulating points.
        /** @type {Pt[]} */
        const points = [];
        /** @type {string|undefined} */
        let firstId;
        let cur = head;
        let curStartSide = headEnter;
        let safety = walls.length + 1;
        while (cur != null && !visited.has(cur) && safety-- > 0) {
            visited.add(cur);
            if (firstId === undefined) firstId = walls[cur].id;
            const wallPts = walls[cur].points;
            const ordered = curStartSide === 0 ? wallPts : wallPts.slice().reverse();
            if (points.length === 0) for (const p of ordered) points.push(p);
            else for (let p = 1; p < ordered.length; p++) points.push(ordered[p]);
            const exitSide = /** @type {0|1} */ (curStartSide === 0 ? 1 : 0);
            const nb = neighbor[cur][exitSide];
            if (!nb) break;
            cur = nb.wi;
            // We enter the next wall at side `nb.side`; that's its chain "start side".
            curStartSide = nb.side;
        }

        merged.push({
            id: firstId ?? walls[head].id,
            brushId: walls[head].brushId,
            points,
        });
    }

    return merged;
}

/**
 * Stable per-position pseudo-random in [0, 1). Used to pick variants so
 * re-renders are deterministic.
 * @param {number} a
 * @param {number} b
 * @param {string} salt
 */
function hash01(a, b, salt) {
    let h = 2166136261 >>> 0;
    for (const ch of salt) h = ((h ^ ch.charCodeAt(0)) * 16777619) >>> 0;
    h = ((h ^ (a | 0)) * 16777619) >>> 0;
    h = ((h ^ (b | 0)) * 16777619) >>> 0;
    return (h >>> 0) / 4294967296;
}

/**
 * Pick a tile from a role bucket. Currently returns the first tile so
 * every vertex/segment of a given role looks identical — FA ships
 * variants that aren't always interchangeable in shape (e.g. rounded vs
 * square outside corners), and lumping them under one bucket made
 * adjacent corners flip styles randomly. Re-introduce variant selection
 * once the manifest tags variants by shape.
 *
 * Args (a, b, salt) are unused but kept on the signature so call sites
 * don't need to change when we restore variant selection.
 *
 * @param {Tile[]} variants
 * @param {number} _a
 * @param {number} _b
 * @param {string} _salt
 */
function pickVariant(variants, _a, _b, _salt) {
    if (!variants || variants.length === 0) return null;
    return variants[0];
}

/**
 * @param {number} a
 * @param {number} b
 */
function gcd(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b) [a, b] = [b, a % b];
    return a;
}

/**
 * Direction from p to q: 'e','w','n','s' for axis-aligned, 'ne','nw','se','sw'
 * for 45° diagonals, and an *angled* key `a:<dx>,<dy>` (reduced by gcd, so
 * collinear runs share it — (0,0)→(3,1) and (0,0)→(6,2) are both `a:3,1`) for
 * any other segment between two grid intersections. Returns null for a
 * zero-length segment.
 * @param {Pt} p
 * @param {Pt} q
 */
function dir(p, q) {
    const rx = q.x - p.x;
    const ry = q.y - p.y;
    if (rx !== 0 && ry !== 0 && Math.abs(rx) !== Math.abs(ry)) {
        const g = gcd(rx, ry);
        return `a:${rx / g},${ry / g}`;
    }
    const dx = Math.sign(rx);
    const dy = Math.sign(ry);
    if (dx === 0 && dy === 0) return null;
    if (dx === 0) return dy > 0 ? 's' : 'n';
    if (dy === 0) return dx > 0 ? 'e' : 'w';
    if (dx > 0 && dy < 0) return 'ne';
    if (dx > 0 && dy > 0) return 'se';
    if (dx < 0 && dy < 0) return 'nw';
    return 'sw'; // dx < 0 && dy > 0
}

/** True for an angled (neither axis nor 45°) direction key from `dir()`. */
function isAngledDir(d) {
    return d.startsWith('a:');
}

/** True for the 4 axis (cardinal) direction codes. */
function isAxisDir(d) {
    return d === 'e' || d === 'w' || d === 'n' || d === 's';
}

/**
 * The reduced (dx, dy) step of an angled direction key.
 * @param {string} d  `a:<dx>,<dy>`
 * @returns {[number, number]}
 */
function angledStep(d) {
    const [dx, dy] = d.slice(2).split(',').map(Number);
    return [dx, dy];
}

/**
 * Rotation (radians) so a canonical-east straight tile faces the given
 * segment direction.
 * @param {string} d
 */
function straightAngle(d) {
    if (isAngledDir(d)) {
        const [dx, dy] = angledStep(d);
        return Math.atan2(dy, dx);
    }
    switch (d) {
        case 'e':
            return 0;
        case 'se':
            return Math.PI / 4;
        case 's':
            return Math.PI / 2;
        case 'sw':
            return (3 * Math.PI) / 4;
        case 'w':
            return Math.PI;
        case 'nw':
            return (-3 * Math.PI) / 4;
        case 'n':
            return -Math.PI / 2;
        case 'ne':
            return -Math.PI / 4;
        default:
            return 0;
    }
}

/**
 * Unit vector for a direction code. Diagonals normalize so the vector
 * has length 1 (each component = ±1/√2).
 * @param {string} d
 * @returns {[number, number]}
 */
function unitVec(d) {
    if (isAngledDir(d)) {
        const [dx, dy] = angledStep(d);
        const len = Math.hypot(dx, dy);
        return [dx / len, dy / len];
    }
    const s = Math.SQRT1_2;
    switch (d) {
        case 'e':
            return [1, 0];
        case 'w':
            return [-1, 0];
        case 's':
            return [0, 1];
        case 'n':
            return [0, -1];
        case 'ne':
            return [s, -s];
        case 'nw':
            return [-s, -s];
        case 'se':
            return [s, s];
        case 'sw':
            return [-s, s];
        default:
            return [1, 0];
    }
}

/** True for the 4 diagonal direction codes. */
function isDiagonalDir(d) {
    return d === 'ne' || d === 'nw' || d === 'se' || d === 'sw';
}

/** Opposite direction (cardinal, diagonal or angled). */
function oppositeDir(d) {
    if (isAngledDir(d)) {
        const [dx, dy] = angledStep(d);
        return `a:${-dx},${-dy}`;
    }
    switch (d) {
        case 'e':
            return 'w';
        case 'w':
            return 'e';
        case 's':
            return 'n';
        case 'n':
            return 's';
        case 'ne':
            return 'sw';
        case 'sw':
            return 'ne';
        case 'nw':
            return 'se';
        case 'se':
            return 'nw';
        default:
            return d;
    }
}

/**
 * Classify each unit cell of an axis segment for the inset (no-overlap)
 * straight path. A cell whose start or end grid point is an *inset vertex* (a
 * Corner/Joint/Connector node where pass 3a draws a vertex tile) is a boundary
 * cell: it renders as a half-block (`Straight_D`) with its wall material on the
 * outer half — the half pointing AWAY from the vertex — so the vertex piece
 * sits flush in its own ±0.5 cell instead of overlapping the flanking run. A
 * cell inset at BOTH ends is fully covered by the two vertex tiles' inner
 * halves and emits nothing. Every other cell is a normal full straight cell.
 *
 * `dOut` on a half cell is the direction the material points (away from the
 * inset vertex): forward `d` when the cell's start is the vertex, `opposite(d)`
 * when its end is. In both single-inset cases the half-block is centered on the
 * cell; only the rotation differs.
 *
 * @param {Pt} a segment start (grid coords)
 * @param {string} d cardinal segment direction ('n'|'e'|'s'|'w')
 * @param {number} segLen segment length in cells
 * @param {Set<string>} [insetKeys] inset vertex grid points, keyed "x,y"
 * @returns {Array<{type:'normal'}|{type:'skip'}|{type:'half',dOut:string}>}
 *   one descriptor per cell in run order (length === segLen)
 */
export function classifyStraightCells(a, d, segLen, insetKeys) {
    const [ux, uy] = unitVec(d);
    const opp = oppositeDir(d);
    const isInset = (c) => !!insetKeys && insetKeys.has(`${a.x + ux * c},${a.y + uy * c}`);
    /** @type {Array<{type:'normal'}|{type:'skip'}|{type:'half',dOut:string}>} */
    const out = [];
    for (let c = 0; c < segLen; c++) {
        const startInset = isInset(c);
        const endInset = isInset(c + 1);
        if (startInset && endInset) out.push({ type: 'skip' });
        else if (startInset) out.push({ type: 'half', dOut: d });
        else if (endInset) out.push({ type: 'half', dOut: opp });
        else out.push({ type: 'normal' });
    }
    return out;
}

/**
 * Orientation of the `Connector_DIAG` elbow at a diagonal↔axis junction, keyed
 * by `"<axisArm>:<diagArm>"` — the direction the axis wall leaves the junction
 * and the direction the diagonal leaves it. Canonical `Connector_DIAG_A` has its
 * axis arm pointing EAST and its diagonal arm pointing SW; every other junction
 * is that rotated (and/or horizontally flipped, since rotation alone can't
 * produce the mirror pair). The four E/W-axis entries are verified against the
 * bench; the N/S-axis entries are the 90° rotations.
 *
 * @type {Record<string, {angle: number, flip: boolean}>}
 */
const DIAG_ELBOW = {
    'e:sw': { angle: 0, flip: false },
    'w:se': { angle: 0, flip: true },
    'w:ne': { angle: Math.PI, flip: false },
    'e:nw': { angle: Math.PI, flip: true },
    's:nw': { angle: Math.PI / 2, flip: false },
    'n:se': { angle: -Math.PI / 2, flip: false },
    'n:sw': { angle: -Math.PI / 2, flip: true },
    's:ne': { angle: Math.PI / 2, flip: true },
};

/**
 * Rotation for an Ending tile placed at the polyline's endpoint vertex.
 * Canonical: cap accent is at the tile center (left edge of the wall
 * material rectangle, which spans tile-local x=100..146 vertically
 * around y=100); the wall material extends EAST from the cap. To cap a
 * wall extending in direction d, rotate so canonical-east aligns with d
 * (so the wall material extends back into the wall body and the cap
 * sits exactly at the polyline endpoint).
 * @param {string} d  direction the wall extends from the endpoint
 */
function endingAngle(d) {
    switch (d) {
        case 'e':
            return 0;
        case 's':
            return Math.PI / 2;
        case 'w':
            return Math.PI;
        case 'n':
            return -Math.PI / 2;
        default:
            return 0;
    }
}

/**
 * Rotation for a Corner tile at an interior bend. Canonical Corner_A is
 * an "ES" corner — wall material in the SE quadrant of the tile,
 * connecting the east and south edges. The four bend orientations are
 * 90° rotations (CW) of that:
 *   ES (canonical): walls exit east + south
 *   SW (+90° CW):   walls exit south + west
 *   WN (+180°):     walls exit west + north
 *   NE (+270° CW):  walls exit north + east
 *
 * @param {string} dIn  direction wall traveled to arrive at this vertex
 * @param {string} dOut direction wall travels to leave this vertex
 */
function cornerAngle(dIn, dOut) {
    const key = dIn + dOut;
    switch (key) {
        // ES shape — neighbors are east + south (canonical).
        case 'ws':
        case 'ne':
            return 0;
        // SW shape — neighbors are south + west.
        case 'es':
        case 'nw':
            return Math.PI / 2;
        // WN shape — neighbors are west + north.
        case 'en':
        case 'sw':
            return Math.PI;
        // NE shape — neighbors are north + east.
        case 'se':
        case 'wn':
            return -Math.PI / 2;
        default:
            return 0;
    }
}

/**
 * Greedy longest-fit-first packing of `length` cells using the
 * available straight tile widths (1, 2, 3 cells). With a hash-driven
 * tweak so identical-length runs don't always look the same.
 * Returns a list of widths in cells.
 * @param {number} length
 * @param {number[]} availableWidths
 * @param {number} a
 * @param {number} b
 */
function packLengths(length, availableWidths, a, b) {
    const widths = [...availableWidths].sort((x, y) => y - x); // largest first
    /** @type {number[]} */
    const out = [];
    let remaining = length;
    let step = 0;
    while (remaining > 0) {
        // Bias toward longer pieces but occasionally drop one size for variety.
        const r = hash01(a, b + step, 'pack');
        let chosen = -1;
        for (const w of widths) {
            if (w <= remaining) {
                // 70% take the longest fit; otherwise fall through to the next size.
                if (chosen === -1 && r >= 0.3) {
                    chosen = w;
                    break;
                }
                if (chosen === -1) {
                    chosen = w;
                    continue;
                } // chosen will be shorter on next loop
                chosen = w;
                break;
            }
        }
        if (chosen === -1) chosen = 1;
        out.push(chosen);
        remaining -= chosen;
        step++;
    }
    return out;
}

/**
 * Draw an image rotated around its center, optionally horizontally
 * flipped. The flip happens AFTER the rotation so it's a horizontal
 * mirror in the rotated frame — used by the diagonal-end connector,
 * which is the canonical asset reflected across the wall's diagonal
 * axis (rotation alone can't produce the reflected orientation).
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLImageElement} img
 * @param {number} cx  world-x center
 * @param {number} cy  world-y center
 * @param {number} w   destination width in world px
 * @param {number} h   destination height in world px
 * @param {number} angle radians
 * @param {boolean} [flipH=false]
 * @param {number} [trimEnds=0] fraction of the source width cropped off each
 *   end (left and right) before drawing — drops a tile's end lines
 */
function drawRotated(ctx, img, cx, cy, w, h, angle, flipH = false, trimEnds = 0) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    if (flipH) ctx.scale(-1, 1);
    if (trimEnds > 0) {
        const sw = img.naturalWidth || img.width;
        const sh = img.naturalHeight || img.height;
        ctx.drawImage(img, sw * trimEnds, 0, sw * (1 - 2 * trimEnds), sh, -w / 2, -h / 2, w, h);
    } else {
        ctx.drawImage(img, -w / 2, -h / 2, w, h);
    }
    ctx.restore();
}

/**
 * Rotation for a Joint (T-junction) tile. Canonical Joint_A has walls
 * extending E + W + S — the empty arm points NORTH. To orient a T whose
 * empty arm points in `missingDir`, rotate so the canonical-N points
 * that way.
 * @param {string} missingDir  'n' | 'e' | 's' | 'w'
 */
function jointAngle(missingDir) {
    switch (missingDir) {
        case 'n':
            return 0;
        case 'e':
            return Math.PI / 2;
        case 's':
            return Math.PI;
        case 'w':
            return -Math.PI / 2;
        default:
            return 0;
    }
}

/**
 * Rotation for a Corner tile, given a 2-direction set. The set must
 * contain exactly two perpendicular cardinals. Wraps cornerAngle()'s
 * dIn/dOut form by picking a consistent traversal.
 * @param {Set<string>} dirSet
 */
function cornerAngleFromSet(dirSet) {
    const has = (d) => dirSet.has(d);
    if (has('e') && has('s')) return cornerAngle('w', 's');
    if (has('s') && has('w')) return cornerAngle('e', 's');
    if (has('w') && has('n')) return cornerAngle('e', 'n');
    if (has('n') && has('e')) return cornerAngle('w', 'n');
    return 0;
}

/**
 * Given the set of cardinal directions a wall extends from a point,
 * pick the right vertex tile + rotation. Returns null if no tile should
 * be drawn (collinear pass-through, or unsupported configuration).
 *
 * `isDoorAdjacent` switches a 2-perpendicular corner from the default
 * squared Corner_A to the second corner variant (Corner_B = rounded
 * jamb) so that a door framed by perpendicular walls reads as a doorway
 * rather than a hard outside corner.
 *
 * @param {TileBrush} brush
 * @param {Set<string>} dirSet
 * @param {number} x  grid coord
 * @param {number} y  grid coord
 * @param {boolean} isDoorAdjacent
 */
function pickVertexTile(brush, dirSet, x, y, isDoorAdjacent) {
    // FA modular packs only ship 90°-axis-aligned vertex pieces (Corner,
    // Joint, Connector). Skip the vertex tile entirely when any
    // incident direction is a 45° diagonal — the straight tiles at the
    // segment ends just butt against each other. Endings on a single
    // diagonal direction also fall into this skip. An angled (non-45°)
    // run is skipped the same way — no vertex piece fits it either.
    for (const d of dirSet) if (!isAxisDir(d)) return null;

    const n = dirSet.size;

    if (n === 1) {
        const [d] = dirSet;
        const tile = pickVariant(brush.pieces.ending, x, y, 'ending');
        return tile ? { tile, angle: endingAngle(d) } : null;
    }

    if (n === 2) {
        const collinear =
            (dirSet.has('e') && dirSet.has('w')) || (dirSet.has('n') && dirSet.has('s'));
        if (collinear) return null; // segment tiles cover this point
        const corners = brush.pieces.corner;
        if (!corners || corners.length === 0) return null;
        // Door-adjacent corners use the rounded variant (Corner_B) so the
        // door reads as a doorway with rounded jambs instead of a sharp
        // outside corner. Falls back to the default if the brush only
        // ships one corner.
        const tile = isDoorAdjacent && corners.length > 1 ? corners[1] : corners[0];
        return { tile, angle: cornerAngleFromSet(dirSet) };
    }

    if (n === 3) {
        const all = ['n', 'e', 's', 'w'];
        const missing = all.find((d) => !dirSet.has(d));
        const joints = brush.pieces.joint;
        if (!joints || joints.length === 0 || !missing) {
            // Brush has no joint piece — fall back to a corner so we at least
            // cover the vertex visually.
            const tile = pickVariant(brush.pieces.corner, x, y, 'corner');
            return tile ? { tile, angle: 0 } : null;
        }
        const tile = pickVariant(joints, x, y, 'joint');
        return tile ? { tile, angle: jointAngle(missing) } : null;
    }

    if (n === 4) {
        const connectors = brush.pieces.connector;
        if (!connectors || connectors.length === 0) return null;
        const tile = pickVariant(connectors, x, y, 'connector');
        return tile ? { tile, angle: 0 } : null;
    }

    return null;
}

/**
 * How a packed straight tile of `len` cells is rendered when a crossing /
 * T-junction lands strictly in its interior. A T/cross is built from the
 * Joint/Connector piece (drawn at the vertex in pass 3a) flanked by 1-cell
 * straights — a 2-cell piece can't sit against the junction because its
 * off-grid half won't line up under the connector. So a tile that a wall
 * crosses is rendered entirely as 1x cells; an uncrossed tile stays whole.
 *
 * `advance` is the tile's start offset (in cells) along the segment from `a`;
 * `junctionKeys` holds the "x,y" grid points that are crossings.
 *
 * @param {number} len tile width in cells
 * @param {number} advance tile start offset along the segment, in cells
 * @param {Pt} a segment start (grid coords)
 * @param {number} ux unit direction x (cardinal)
 * @param {number} uy unit direction y (cardinal)
 * @param {Set<string>} [junctionKeys] crossing grid points, keyed "x,y"
 * @returns {number[]} sub-widths in run order (they sum to `len`)
 */
export function splitTileAtJunctions(len, advance, a, ux, uy, junctionKeys) {
    if (!junctionKeys || junctionKeys.size === 0 || len < 2) return [len];
    for (let j = 1; j < len; j++) {
        if (junctionKeys.has(`${a.x + ux * (advance + j)},${a.y + uy * (advance + j)}`)) {
            return Array(len).fill(1);
        }
    }
    return [len];
}

/**
 * Pass 1: straight tiles along each unit segment of one wall.
 *
 * Two paths. When the brush ships a `straightHalf` piece (`Straight_D`) the run
 * is drawn **inset**: cells adjacent to a Corner/Joint/Connector node
 * (`insetKeys`) become half-blocks so the vertex piece sits flush in its own
 * cell — no overlap (`drawInsetStraightSegment`). Without that piece the run is
 * drawn full-length up to each node and `splitTileAtJunctions` breaks a crossed
 * tile so the seam lands under the vertex piece — the prior (overlapping)
 * behavior, kept for brushes that haven't adopted the half-block.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{id:string,brushId:string,points:Pt[]}} wall
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 * @param {Set<string>} [junctionKeys] crossing/T-junction grid points ("x,y")
 * @param {Set<string>} [insetKeys] inset vertex grid points ("x,y"), set only
 *   when the brush has a `straightHalf` piece
 * @param {Array<{gx:number,gy:number,angle:number}>} [seams] collector for seam
 *   connectors, drawn in pass 4 (after the vertex pieces) so they sit on top
 * @param {Map<string, Join>} [joins] angled-run joins (see buildAngledJoins);
 *   a segment touching one is clipped to its sector there
 */
function drawWallStraights(
    ctx,
    wall,
    brush,
    textures,
    cellPx,
    junctionKeys,
    insetKeys,
    seams,
    joins,
) {
    const points = simplifyPolyline(wall.points);
    if (points.length < 2) return;

    const distinctWidths = [...new Set(brush.pieces.straight.map((t) => t.cellsW))];
    const last = points.length - 1;

    for (let i = 0; i < last; i++) {
        const a = points[i];
        const b = points[i + 1];
        const d = dir(a, b);
        if (!d) continue;
        const segLen = Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y));
        if (segLen <= 0) continue;

        // Diagonals and angled runs are handled by separate passes
        // (drawDiagonalBridge, drawDiagonalConnectors, drawAngledRun) so the
        // global z-order can be: axis-straights → diagonal-bridges/angled-runs
        // → connectors.
        if (!isAxisDir(d)) continue;

        drawJoinedSegment(ctx, joins, a, b, d, brush, textures, cellPx, seams, () =>
            drawAxisSegment(
                ctx,
                a,
                d,
                segLen,
                i,
                brush,
                textures,
                cellPx,
                distinctWidths,
                junctionKeys,
                insetKeys,
                seams,
            ),
        );
    }
}

/**
 * Pass 1 for one axis segment of a wall: inset (half-blocked) when the brush
 * ships a `straightHalf` piece, full-length otherwise. See drawWallStraights.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Pt} a segment start (grid coords)
 * @param {string} d cardinal segment direction
 * @param {number} segLen segment length in cells
 * @param {number} i segment index in the polyline
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 * @param {number[]} distinctWidths available straight widths
 * @param {Set<string>} [junctionKeys] crossing/T-junction grid points ("x,y")
 * @param {Set<string>} [insetKeys] inset vertex grid points ("x,y")
 * @param {Array<{gx:number,gy:number,angle:number}>} [seams] seam collector
 */
function drawAxisSegment(
    ctx,
    a,
    d,
    segLen,
    i,
    brush,
    textures,
    cellPx,
    distinctWidths,
    junctionKeys,
    insetKeys,
    seams,
) {
    // Inset path: half-block the cells flanking each vertex so nothing overlaps
    // the Corner/Joint/Connector piece. Interior junctions are always inset
    // (they're in insetKeys), so a run never spans one — splitTileAtJunctions is
    // unneeded here.
    if (insetKeys) {
        drawInsetStraightSegment(
            ctx,
            a,
            d,
            segLen,
            brush,
            textures,
            cellPx,
            distinctWidths,
            insetKeys,
            seams,
        );
        return;
    }

    const lengths = packLengths(segLen, distinctWidths, a.x + i, a.y);
    const [ux, uy] = unitVec(d);
    const angle = straightAngle(d);

    // Draw each packed tile, but break it wherever a crossing / T-junction
    // lands in its interior (splitTileAtJunctions), so a 3x that a wall crosses
    // renders as a 1x/2x combination with a seam exactly at the crossing —
    // where pass 3a draws the joint/connector piece.
    let advance = 0;
    for (let k = 0; k < lengths.length; k++) {
        for (const w of splitTileAtJunctions(lengths[k], advance, a, ux, uy, junctionKeys)) {
            const cx = a.x + ux * advance;
            const cy = a.y + uy * advance;
            const candidates = brush.pieces.straight.filter((t) => t.cellsW === w);
            const tile = pickVariant(candidates, cx, cy, `straight-${w}`);
            if (tile) {
                drawTileAt(
                    ctx,
                    cx + ux * (w / 2),
                    cy + uy * (w / 2),
                    tile,
                    brush,
                    textures,
                    cellPx,
                    angle,
                );
            }
            advance += w;
        }
    }

    // Collect a seam connector for each INTERNAL straight-to-straight PACK seam.
    // Drawn later (pass 4, after the vertex pieces) so the overlay always sits
    // on top. A brush with no connectorStraight piece collects nothing, as does
    // one that opts out via `useStraightSeamConnector: false` (a "segmented"
    // style whose straights should abut as hard block edges even though a
    // Connector_A tile is present in the piece set).
    const seamPieces = brush.pieces.connectorStraight;
    if (seams && seamPieces && seamPieces.length > 0 && brush.useStraightSeamConnector !== false) {
        let seam = 0;
        for (let k = 0; k < lengths.length - 1; k++) {
            seam += lengths[k];
            seams.push({ gx: a.x + ux * seam, gy: a.y + uy * seam, angle });
        }
    }
}

/**
 * Overlay a straight-seam connector tile centred at a world grid point, rotated
 * to the run. No-op when the brush ships no seam piece. Bridges the butt joints
 * along an inset run: pack seams within a run, and the half-block's joints with
 * the run and with the vertex piece.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} gx world cell x
 * @param {number} gy world cell y
 * @param {Tile[]|undefined} seamPieces
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 * @param {number} angle radians (run direction)
 */
function drawSeamConnector(ctx, gx, gy, seamPieces, brush, textures, cellPx, angle) {
    const tile = pickVariant(seamPieces, gx, gy, 'connector-straight');
    if (tile) drawTileAt(ctx, gx, gy, tile, brush, textures, cellPx, angle);
}

/**
 * Pass 1, inset variant: draw one axis segment with the flanking cells at each
 * Corner/Joint/Connector node rendered as `Straight_D` half-blocks, so the
 * vertex piece sits flush in its own cell with nothing overlapping it.
 *
 * The segment is classified cell-by-cell (`classifyStraightCells`): boundary
 * cells become half-blocks (material pointing away from the vertex), cells inset
 * at both ends emit nothing, and maximal runs of normal cells are packed
 * greedily exactly as the full-length path packs a whole segment. The seam
 * connector overlays only internal pack seams *within* a normal run — never at a
 * run boundary, where a vertex piece or half-block already sits.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Pt} a segment start (grid coords)
 * @param {string} d cardinal segment direction
 * @param {number} segLen segment length in cells
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 * @param {number[]} distinctWidths available straight widths (desc from caller)
 * @param {Set<string>} insetKeys inset vertex grid points ("x,y")
 * @param {Array<{gx:number,gy:number,angle:number}>} [seams] collector for seam
 *   connectors, drawn in pass 4 (after the vertex pieces) so they sit on top
 */
function drawInsetStraightSegment(
    ctx,
    a,
    d,
    segLen,
    brush,
    textures,
    cellPx,
    distinctWidths,
    insetKeys,
    seams,
) {
    const [ux, uy] = unitVec(d);
    const angle = straightAngle(d);
    const cells = classifyStraightCells(a, d, segLen, insetKeys);

    const seamPieces = brush.pieces.connectorStraight;
    const collectSeams =
        !!seams &&
        !!seamPieces &&
        seamPieces.length > 0 &&
        brush.useStraightSeamConnector !== false;

    let c = 0;
    while (c < segLen) {
        const cell = cells[c];

        if (cell.type === 'skip') {
            c += 1;
            continue;
        }

        if (cell.type === 'half') {
            // Half-block sits at the boundary cell's center. FA's Straight_D carries
            // its wall material on the WEST (−x) half at angle 0, so rotating by
            // straightAngle(dOut) alone would point the material toward the vertex
            // (wrong). Add π so the material lands on the outer half, pointing along
            // dOut, away from the vertex. (Straight_D is the only asymmetric straight;
            // the full-width straights are rotation-invariant.)
            const cx = a.x + ux * (c + 0.5);
            const cy = a.y + uy * (c + 0.5);
            const tile = pickVariant(
                brush.pieces.straightHalf,
                a.x + ux * c,
                a.y + uy * c,
                'straight-half',
            );
            if (tile) {
                drawTileAt(
                    ctx,
                    cx,
                    cy,
                    tile,
                    brush,
                    textures,
                    cellPx,
                    straightAngle(cell.dOut) + Math.PI,
                );
            }
            // Seam connectors bridge the two butt-joints a half-block introduces: one
            // at the tile centre where it meets the vertex piece (half-cell line), one
            // at the outer grid boundary where it meets the run. `dOut` points along
            // the run, away from the vertex, so the vertex sits on the −dOut side and
            // the run on the +dOut side. Collected for pass 4 so the vertex-side seam
            // lands ON TOP of the Corner/Joint/Connector piece, not under it.
            if (collectSeams) {
                // Vertex-side seam (half-block ↔ Corner/Joint/Connector) at the centre.
                seams.push({ gx: cx, gy: cy, angle });
                // Run-side seam (half-block ↔ straight) only when a normal cell abuts.
                const forward = cell.dOut === d;
                const neighbor = forward ? cells[c + 1] : cells[c - 1];
                if (neighbor && neighbor.type === 'normal') {
                    const oa = forward ? c + 1 : c;
                    seams.push({ gx: a.x + ux * oa, gy: a.y + uy * oa, angle });
                }
            }
            c += 1;
            continue;
        }

        // Maximal run of normal cells → pack greedily from the run's start.
        const runStart = c;
        while (c < segLen && cells[c].type === 'normal') c += 1;
        const runLen = c - runStart;
        const gx = a.x + ux * runStart;
        const gy = a.y + uy * runStart;
        const lengths = packLengths(runLen, distinctWidths, gx, gy);

        let advance = 0;
        for (let k = 0; k < lengths.length; k++) {
            const w = lengths[k];
            const tcx = gx + ux * advance;
            const tcy = gy + uy * advance;
            const candidates = brush.pieces.straight.filter((t) => t.cellsW === w);
            const tile = pickVariant(candidates, tcx, tcy, `straight-${w}`);
            if (tile) {
                drawTileAt(
                    ctx,
                    tcx + ux * (w / 2),
                    tcy + uy * (w / 2),
                    tile,
                    brush,
                    textures,
                    cellPx,
                    angle,
                );
            }
            advance += w;
        }

        if (collectSeams) {
            let seam = 0;
            for (let k = 0; k < lengths.length - 1; k++) {
                seam += lengths[k];
                seams.push({ gx: gx + ux * seam, gy: gy + uy * seam, angle });
            }
        }
    }
}

/**
 * Iterate diagonal segments of a wall and call `cb` for each. Used
 * by the diagonal-bridge and diagonal-connector passes so they
 * share the same iteration without re-calling simplifyPolyline.
 *
 * `prevD`/`nextD` are the directions of the neighbor polyline segments at the
 * start/end vertex (null at a free polyline end). A `Connector_DIAG` elbow is
 * drawn only where a neighbor is an AXIS (cardinal) run; its orientation is
 * derived from that axis arm plus the diagonal arm.
 *
 * @param {{id:string,brushId:string,points:Pt[]}} wall
 * @param {(a: Pt, b: Pt, d: string, segLen: number, i: number, prevD: string|null, nextD: string|null) => void} cb
 */
function forEachDiagonalSegment(wall, cb) {
    const points = simplifyPolyline(wall.points);
    if (points.length < 2) return;
    const last = points.length - 1;
    for (let i = 0; i < last; i++) {
        const a = points[i];
        const b = points[i + 1];
        const d = dir(a, b);
        if (!d || !isDiagonalDir(d)) continue;
        const segLen = Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y));
        if (segLen <= 0) continue;
        const prevD = i > 0 ? dir(points[i - 1], points[i]) : null;
        const nextD = i + 2 <= last ? dir(points[i + 1], points[i + 2]) : null;
        cb(a, b, d, segLen, i, prevD, nextD);
    }
}

/**
 * Render a 45° diagonal wall segment.
 *
 * Geometry (per FA modular wall convention):
 *   - One `Connector_DIAG_A_1x1` tile is placed centered on each
 *     endpoint vertex. The asset depicts a vertex-to-diagonal joiner
 *     — wall material extending from the vertex inward toward the
 *     diagonal axis. Two connectors per diagonal segment is the
 *     minimum.
 *   - For length > 1 vertex-steps, the gap between the two connectors
 *     is bridged by straight tiles rotated to the diagonal axis at
 *     their native length (no stretching). The bridge spans
 *     `(segLen - 1)` cell-steps of axis length.
 *
 * The two connectors at A and B are rotated by 180° from each other
 * so their wall material extends in opposite directions along the
 * diagonal (toward each other when length=1, toward the bridge
 * otherwise).
 *
 * If the brush ships no `connectorDiag` piece, falls back to using
 * straight tiles stretched by √2 so the segment is at least visible.
 */
/**
 * Normalize a diagonal segment to its canonical form (NE or SE) by
 * swapping endpoints when the segment was drawn in the reverse
 * direction. Returns the canonical (a, b, d) along with the clip
 * flags that now track the physical vertex.
 */
function normalizeDiagSegment(a, b, d, clipA, clipB) {
    if (d === 'sw')
        return { a: b, b: a, d: /** @type {'ne'} */ ('ne'), clipA: clipB, clipB: clipA };
    if (d === 'nw')
        return { a: b, b: a, d: /** @type {'se'} */ ('se'), clipA: clipB, clipB: clipA };
    return { a, b, d, clipA, clipB };
}

/**
 * Diagonal bridge pass — rotated straight tiles between the two
 * endpoint vertices, one 1-cell tile per diagonal cell-step. No
 * connectors are drawn here; that's a separate pass so the global
 * z-order is "axis straights → diagonal bridges → connectors".
 */
function drawDiagonalBridge(ctx, a, b, d, segLen, segIndex, brush, textures, cellPx, seams) {
    const norm = normalizeDiagSegment(a, b, d, false, false);
    a = norm.a;
    d = norm.d; // `b` isn't needed — the bridge steps out from `a`

    // One straight tile per diagonal grid-step, stretched √2 along the wall axis
    // so it spans that step exactly (a step from (x,y)→(x+1,y+1) is √2 long). The
    // run therefore covers the diagonal end to end with no gaps — earlier it used
    // unit-length steps, so the bar under-spanned by (√2−1) per step and detached
    // from the endpoints.
    const sx = 1; // canonical 'ne'/'se' both have +x component
    const sy = d === 'se' ? 1 : -1;
    const angle = straightAngle(d);

    for (let k = 0; k < segLen; k++) {
        const candidates = brush.pieces.straight.filter((t) => t.cellsW === 1);
        const tile = pickVariant(candidates, a.x + segIndex + k, a.y + segIndex + k, 'straight-1');
        if (!tile) continue;
        const cx = a.x + (k + 0.5) * sx;
        const cy = a.y + (k + 0.5) * sy;
        drawTileAt(ctx, cx, cy, tile, brush, textures, cellPx, angle, Math.SQRT2);
    }

    // Collect a seam connector at each internal grid-step boundary (drawn in pass
    // 4, on top) so a continuous brush reads unbroken along the diagonal too.
    const seamPieces = brush.pieces.connectorStraight;
    if (seams && seamPieces && seamPieces.length > 0 && brush.useStraightSeamConnector !== false) {
        for (let k = 1; k < segLen; k++) {
            seams.push({ gx: a.x + k * sx, gy: a.y + k * sy, angle });
        }
    }
}

/**
 * Diagonal connector pass — places the `Connector_DIAG` elbow (the axis→diagonal
 * transition piece) at a segment endpoint ONLY when that endpoint is a
 * diagonal↔axis junction (`prevD`/`nextD` is an axis run). A free diagonal end — or a
 * diagonal↔diagonal join — gets no elbow; the √2 bridge tile caps it.
 */
function drawDiagonalConnectors(ctx, a, b, d, segIndex, brush, textures, cellPx, prevD, nextD) {
    const diagPieces = brush.pieces.connectorDiag;
    if (!diagPieces || diagPieces.length === 0) return;

    // Vertex a: the diagonal leaves in direction d; the axis arm points away from
    // the incoming axis segment (opposite of prevD). Vertex b: the diagonal leaves
    // back toward a (opposite of d); the axis arm is the outgoing segment (nextD).
    if (prevD && isAxisDir(prevD)) {
        drawDiagElbow(ctx, a, oppositeDir(prevD), d, diagPieces, brush, textures, cellPx, segIndex);
    }
    if (nextD && isAxisDir(nextD)) {
        drawDiagElbow(ctx, b, nextD, oppositeDir(d), diagPieces, brush, textures, cellPx, segIndex);
    }
}

/**
 * Draw the diagonal elbow at vertex `v`, oriented by its axis arm + diagonal arm
 * (see DIAG_ELBOW). No-op for an arm pair the table doesn't cover.
 */
function drawDiagElbow(ctx, v, axisArm, diagArm, diagPieces, brush, textures, cellPx, segIndex) {
    const cfg = DIAG_ELBOW[`${axisArm}:${diagArm}`];
    if (!cfg) return;
    const tile = pickVariant(diagPieces, v.x + segIndex, v.y + segIndex, 'diag');
    if (tile) drawTileAt(ctx, v.x, v.y, tile, brush, textures, cellPx, cfg.angle, 1, cfg.flip);
}

/**
 * Fallback diagonal renderer for brushes without a connectorDiag
 * piece — stretch axial straight tiles by √2 along the wall axis so
 * one wall-cell spans one vertex step. Slightly elongated texture
 * but at least visible.
 */
function drawDiagonalStraightsFallback(ctx, a, d, segLen, segIndex, brush, textures, cellPx) {
    const widths = brush.pieces.straight.map((t) => t.cellsW);
    const distinctWidths = [...new Set(widths)];
    const lengths = packLengths(segLen, distinctWidths, a.x + segIndex, a.y);
    const [ux, uy] = unitVec(d);
    const angle = straightAngle(d);

    let advance = 0;
    for (let k = 0; k < lengths.length; k++) {
        const len = lengths[k];
        const candidates = brush.pieces.straight.filter((t) => t.cellsW === len);
        const tile = pickVariant(
            candidates,
            a.x + segIndex + k,
            a.y + segIndex + k,
            `straight-${len}`,
        );
        if (!tile) {
            advance += len;
            continue;
        }
        const centerOffset = (advance + len / 2) * Math.SQRT2;
        const cx = a.x + ux * centerOffset;
        const cy = a.y + uy * centerOffset;
        drawTileAt(ctx, cx, cy, tile, brush, textures, cellPx, angle, Math.SQRT2);
        advance += len;
    }
}

/**
 * Iterate angled (neither axis nor 45°) segments of a wall and call `cb` for
 * each — the angled-run counterpart of forEachDiagonalSegment.
 *
 * @param {{id:string,brushId:string,points:Pt[]}} wall
 * @param {(a: Pt, b: Pt, d: string, i: number) => void} cb
 */
function forEachAngledSegment(wall, cb) {
    const points = simplifyPolyline(wall.points);
    if (points.length < 2) return;
    for (let i = 0; i < points.length - 1; i++) {
        const a = points[i];
        const b = points[i + 1];
        const d = dir(a, b);
        if (d && isAngledDir(d)) cb(a, b, d, i);
    }
}

/**
 * Render an angled wall segment — one between two grid intersections that is
 * neither axis-aligned nor 45°, e.g. (0,0)→(3,1) — as a run at its true angle.
 *
 * The run is √(dx²+dy²) cells long. Straight tiles are packed greedily across
 * the nearest whole number of cells, then every tile is stretched (or squeezed)
 * along the wall axis by the same factor so the run spans the segment exactly,
 * end to end — the way the 45° fallback stretches by √2. Each tile is centred
 * on the segment and rotated to it. No vertex piece is drawn at either end
 * (pickVertexTile skips any angled incidence), so the run butts against its
 * neighbours like a 45° junction with no elbow.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Pt} a segment start (grid coords)
 * @param {Pt} b segment end (grid coords)
 * @param {string} d angled direction key from `dir(a, b)`
 * @param {number} segIndex segment index in the polyline
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 * @param {Array<{gx:number,gy:number,angle:number}>} [seams] collector for seam
 *   connectors, drawn in pass 4 (after the vertex pieces) so they sit on top
 */
function drawAngledRun(ctx, a, b, d, segIndex, brush, textures, cellPx, seams) {
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const cells = Math.max(1, Math.round(length));
    const stretch = length / cells;
    const distinctWidths = [...new Set(brush.pieces.straight.map((t) => t.cellsW))];
    const lengths = packLengths(cells, distinctWidths, a.x + segIndex, a.y);
    const [ux, uy] = unitVec(d);
    const angle = straightAngle(d);

    let advance = 0;
    for (let k = 0; k < lengths.length; k++) {
        const w = lengths[k];
        const candidates = brush.pieces.straight.filter((t) => t.cellsW === w);
        const tile = pickVariant(
            candidates,
            a.x + segIndex + k,
            a.y + segIndex + k,
            `straight-${w}`,
        );
        if (tile) {
            const centerOffset = (advance + w / 2) * stretch;
            drawTileAt(
                ctx,
                a.x + ux * centerOffset,
                a.y + uy * centerOffset,
                tile,
                brush,
                textures,
                cellPx,
                angle,
                stretch,
            );
        }
        advance += w;
    }

    const seamPieces = brush.pieces.connectorStraight;
    if (seams && seamPieces && seamPieces.length > 0 && brush.useStraightSeamConnector !== false) {
        let seam = 0;
        for (let k = 0; k < lengths.length - 1; k++) {
            seam += lengths[k];
            seams.push({ gx: a.x + ux * seam * stretch, gy: a.y + uy * seam * stretch, angle });
        }
    }
}

// Joins at angled runs.
//
// FA ships Corner/Joint/Connector art only for axis vertices (and the diagonal
// elbow for 45°↔axis), so where an angled run meets another wall the join is
// built from geometry instead. The plane around the join point is split into
// one *sector* per incident arm, bounded by the bisectors with its angular
// neighbours, and every segment touching the join is clipped to its own arm's
// sector(s). Straight tiles carry their outline along their long edges only (the
// thin line at a tile's end is covered by the join's end patch), so arms clipped
// along shared bisectors meet seamlessly:
//   2 arms (corner) → a mitered join; an end patch carries each run past the
//                     vertex so the outer corner fills out to the miter line
//                     (bevelled at MITER_REACH_CELLS for a sharp bend).
//   3+ arms (T, Y, crossing) → a merged junction: each wall's outlines inside a
//                     neighbour's sector are clipped away, leaving outlines only
//                     on the outer edges, like the FA cross / T art.
// Joins are built only where an angled arm is incident, so axis and 45° joins
// keep their art and render exactly as before.

/**
 * @typedef {object} Join
 * @property {number} x join point (grid coords; fractional at a mid-segment crossing)
 * @property {number} y
 * @property {Map<string, {lo:number, hi:number}>} sectors per incident direction
 *   key, the angular range [lo, hi] (radians, increasing) the arm owns
 * @property {number|null} outer direction (radians) of the bisector of the gap
 *   wider than π between neighbouring arms — the outside of a corner, where the
 *   miter tip points — or null when every gap is ≤ π
 */

/**
 * A join a segment touches: the arm(s) it owns there (one at an endpoint, both
 * directions where it passes through), and at an endpoint the arm it leaves in.
 * @typedef {{join: Join, arms: string[], endArm?: string}} JoinTouch
 */

/** A join patch reaches at most this far past the join point. */
const MAX_JOIN_EXTENSION_CELLS = 3;
/**
 * Miter limit: the outside of a corner is cut off this far from the join point,
 * perpendicular to the outer bisector, so a sharp bend gets a bevel instead of a
 * long spike (a true miter tip grows as 1/sin(φ/2) as the bend angle φ narrows).
 */
const MITER_REACH_CELLS = 1;
/** How far a join patch reaches into the run (and, at least, past the join point). */
const JOIN_PATCH_OVERLAP_CELLS = 0.1;
/** Fraction of the patch tile's source width trimmed off each end (its end lines). */
const JOIN_PATCH_TRIM = 0.05;
/** Sectors overlap by this much so neighbouring arms leave no anti-aliased seam. */
const SECTOR_OVERLAP_PX = 0.5;
/** Clip polygons reach this far — well past any wall segment. */
const CLIP_REACH_PX = 1e6;
/** Tolerance for locating fractional join points on a segment. */
const JOIN_EPSILON = 1e-6;

/**
 * Join points that involve an angled run, each with its arms' sectors.
 * @param {Map<string, {x:number,y:number,dirs:Set<string>}>} inc
 * @returns {Map<string, Join>}
 */
function buildAngledJoins(inc) {
    /** @type {Map<string, Join>} */
    const joins = new Map();
    for (const [key, entry] of inc) {
        const dirs = [...entry.dirs];
        if (dirs.length < 2 || !dirs.some(isAngledDir)) continue;
        // A straight pass-through (a run cut at its gcd point) needs no join.
        if (dirs.length === 2 && oppositeDir(dirs[0]) === dirs[1]) continue;
        const arms = dirs
            .map((d) => {
                const [ux, uy] = unitVec(d);
                return { d, ang: Math.atan2(uy, ux) };
            })
            .sort((p, q) => p.ang - q.ang);
        const sectors = new Map();
        /** @type {number|null} */
        let outer = null;
        for (let i = 0; i < arms.length; i++) {
            const { d, ang } = arms[i];
            const prev = arms[(i - 1 + arms.length) % arms.length].ang;
            const next = arms[(i + 1) % arms.length].ang;
            const gapPrev = positiveAngle(ang - prev);
            const gapNext = positiveAngle(next - ang);
            sectors.set(d, { lo: ang - gapPrev / 2, hi: ang + gapNext / 2 });
            if (gapNext > Math.PI) outer = ang + gapNext / 2;
        }
        joins.set(key, { x: entry.x, y: entry.y, sectors, outer });
    }
    return joins;
}

/**
 * An angle folded into (0, 2π] — the gap swept going from one arm to the next.
 * @param {number} a radians
 */
function positiveAngle(a) {
    const r = a % (2 * Math.PI);
    return r <= 0 ? r + 2 * Math.PI : r;
}

/**
 * True when p lies on segment a→b strictly between its endpoints, within
 * JOIN_EPSILON (join points from crossings can be fractional).
 * @param {Pt} p
 * @param {Pt} a
 * @param {Pt} b
 */
function liesInsideSegment(p, a, b) {
    const rx = b.x - a.x;
    const ry = b.y - a.y;
    const len2 = rx * rx + ry * ry;
    const t = ((p.x - a.x) * rx + (p.y - a.y) * ry) / len2;
    if (t <= JOIN_EPSILON || t >= 1 - JOIN_EPSILON) return false;
    const cross = rx * (p.y - a.y) - ry * (p.x - a.x);
    return Math.abs(cross) / Math.sqrt(len2) < JOIN_EPSILON;
}

/**
 * Draw one wall segment a→b (direction d) via `draw`, clipped to its sectors at
 * every angled join it touches — its endpoints and any join it passes through —
 * with an end patch (drawJoinPatch) at each endpoint join.
 * A segment touching no join is drawn untouched (no save/clip), so axis and 45°
 * walls render exactly as before.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Map<string, Join>|undefined} joins
 * @param {Pt} a
 * @param {Pt} b
 * @param {string} d direction key of a→b
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 * @param {Array<{gx:number,gy:number,angle:number,joinClips?:JoinTouch[]}>|undefined} seams
 *   seam collector the segment's `draw` pushes to
 * @param {() => void} draw draws the segment's tiles
 */
function drawJoinedSegment(ctx, joins, a, b, d, brush, textures, cellPx, seams, draw) {
    /** @type {JoinTouch[]} */
    const touches = [];
    if (joins && joins.size > 0) {
        const back = oppositeDir(d);
        const atA = joins.get(`${a.x},${a.y}`);
        if (atA) touches.push({ join: atA, arms: [d], endArm: d });
        const atB = joins.get(`${b.x},${b.y}`);
        if (atB) touches.push({ join: atB, arms: [back], endArm: back });
        for (const join of joins.values()) {
            if (liesInsideSegment(join, a, b)) touches.push({ join, arms: [d, back] });
        }
    }
    if (touches.length === 0) {
        draw();
        return;
    }

    const firstSeam = seams ? seams.length : 0;
    ctx.save();
    clipToJoins(ctx, touches, cellPx);
    draw();
    for (const { join, endArm } of touches) {
        if (endArm) drawJoinPatch(ctx, join, endArm, brush, textures, cellPx);
    }
    ctx.restore();
    // Seam connectors collected for this segment are drawn later (pass 4, on top
    // of everything); they carry the segment's clip so a seam that lands on a
    // crossing can't lay its outline across the other wall.
    if (seams) for (let k = firstSeam; k < seams.length; k++) seams[k].joinClips = touches;
}

/**
 * Clip to a segment's sector(s) at each join it touches, plus the miter limit
 * where the join is a corner. Callers bracket with save()/restore().
 * @param {CanvasRenderingContext2D} ctx
 * @param {JoinTouch[]} touches
 * @param {number} cellPx
 */
function clipToJoins(ctx, touches, cellPx) {
    for (const { join, arms } of touches) {
        ctx.beginPath();
        for (const arm of arms) {
            const { lo, hi } = join.sectors.get(arm);
            for (const poly of sectorPolygons(join.x * cellPx, join.y * cellPx, lo, hi)) {
                ctx.moveTo(poly[0][0], poly[0][1]);
                for (let k = 1; k < poly.length; k++) ctx.lineTo(poly[k][0], poly[k][1]);
                ctx.closePath();
            }
        }
        ctx.clip();
        if (join.outer !== null) clipMiter(ctx, join, cellPx);
    }
}

/**
 * Clip off the outside of a corner beyond MITER_REACH_CELLS from the join point,
 * perpendicular to the outer bisector (the miter limit's bevel).
 * @param {CanvasRenderingContext2D} ctx
 * @param {Join} join
 * @param {number} cellPx
 */
function clipMiter(ctx, join, cellPx) {
    const vx = join.x * cellPx;
    const vy = join.y * cellPx;
    const wx = Math.cos(/** @type {number} */ (join.outer));
    const wy = Math.sin(/** @type {number} */ (join.outer));
    const reach = MITER_REACH_CELLS * cellPx;
    const R = CLIP_REACH_PX;
    const poly = clipPolygon(
        [
            [vx - R, vy - R],
            [vx + R, vy - R],
            [vx + R, vy + R],
            [vx - R, vy + R],
        ],
        ([x, y]) => reach - ((x - vx) * wx + (y - vy) * wy),
    );
    ctx.beginPath();
    ctx.moveTo(poly[0][0], poly[0][1]);
    for (let k = 1; k < poly.length; k++) ctx.lineTo(poly[k][0], poly[k][1]);
    ctx.closePath();
    ctx.clip();
}

/**
 * The clip polygons (world px) covering the sector [lo, hi] around (vx, vy),
 * widened by SECTOR_OVERLAP_PX on both sides. A sector up to π wide is one
 * convex polygon (the intersection of its two bounding half-planes); a wider one
 * is the union of those half-planes, returned as two polygons. All polygons share
 * one winding, so a path holding several is their union under the nonzero rule.
 * @param {number} vx
 * @param {number} vy
 * @param {number} lo
 * @param {number} hi
 * @returns {Array<Array<[number, number]>>}
 */
function sectorPolygons(vx, vy, lo, hi) {
    const R = CLIP_REACH_PX;
    /** @type {Array<[number, number]>} */
    const square = [
        [vx - R, vy - R],
        [vx + R, vy - R],
        [vx + R, vy + R],
        [vx - R, vy + R],
    ];
    const cl = Math.cos(lo);
    const sl = Math.sin(lo);
    const ch = Math.cos(hi);
    const sh = Math.sin(hi);
    // ≥ 0 on the far side of the lo boundary (toward increasing angle) / on the
    // near side of the hi boundary, each widened by the overlap.
    const pastLo = ([x, y]) => cl * (y - vy) - sl * (x - vx) + SECTOR_OVERLAP_PX;
    const beforeHi = ([x, y]) => sh * (x - vx) - ch * (y - vy) + SECTOR_OVERLAP_PX;
    if (hi - lo <= Math.PI) return [clipPolygon(clipPolygon(square, pastLo), beforeHi)];
    return [clipPolygon(square, pastLo), clipPolygon(square, beforeHi)];
}

/**
 * Clip a convex polygon to the half-plane where the (affine) `side` is ≥ 0
 * (Sutherland–Hodgman, one edge).
 * @param {Array<[number, number]>} poly
 * @param {(p: [number, number]) => number} side
 * @returns {Array<[number, number]>}
 */
function clipPolygon(poly, side) {
    /** @type {Array<[number, number]>} */
    const out = [];
    for (let i = 0; i < poly.length; i++) {
        const p = poly[i];
        const q = poly[(i + 1) % poly.length];
        const sp = side(p);
        const sq = side(q);
        if (sp >= 0) out.push(p);
        if (sp >= 0 !== sq >= 0) {
            const t = sp / (sp - sq);
            out.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]);
        }
    }
    return out;
}

/**
 * Patch an arm's end at a join point. Straight tiles carry a thin dark line at
 * each end (along a run the seam connector hides it), so a run that simply stops
 * at the join would show that line inside its sector. The patch is the 1-cell
 * straight with its end columns trimmed off at the source (so it brings no end
 * line of its own), drawn over the run's end from just inside the run to past
 * the join point — far enough past to fill the arm's sector out to the miter
 * line where the sector reaches behind the vertex (the outside of a corner).
 * The sector clip trims it.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Join} join
 * @param {string} arm direction the segment leaves the join in
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 */
function drawJoinPatch(ctx, join, arm, brush, textures, cellPx) {
    const tile = brush.pieces.straight.reduce((p, q) => (q.cellsW < p.cellsW ? q : p));
    const [ux, uy] = unitVec(arm);
    const ang = Math.atan2(uy, ux);
    const { lo, hi } = join.sectors.get(arm);
    // A sector edge at β > 90° from the arm meets the tile's outer edge behind the
    // join point, at distance halfH·cot(π − β) back along the arm.
    const halfH = tile.cellsH / 2;
    const behind = (beta) => (beta > Math.PI / 2 ? halfH / Math.tan(Math.PI - beta) : 0);
    const back = Math.min(
        MAX_JOIN_EXTENSION_CELLS,
        Math.max(JOIN_PATCH_OVERLAP_CELLS, behind(hi - ang), behind(ang - lo)),
    );
    const length = back + JOIN_PATCH_OVERLAP_CELLS;
    const offset = (JOIN_PATCH_OVERLAP_CELLS - back) / 2; // patch centre along the arm
    drawTileAt(
        ctx,
        join.x + ux * offset,
        join.y + uy * offset,
        tile,
        brush,
        textures,
        cellPx,
        straightAngle(arm),
        length / tile.cellsW,
        false,
        JOIN_PATCH_TRIM,
    );
}

/**
 * Build a global vertex incidence map across a list of walls. Keys are
 * "x,y" stringified grid coords; entries hold the original coord plus
 * the set of cardinal directions in which a wall extends from that
 * point (across all walls in the input).
 *
 * @param {Array<{id:string,brushId:string,points:Pt[]}>} walls
 */
function buildVertexIncidence(walls) {
    /** @type {Map<string, {x:number,y:number,dirs:Set<string>}>} */
    const inc = new Map();

    // Pass 1: directions contributed by each polyline's stored
    // vertices. Use raw points (not simplified) so collinear interior
    // vertices still register — they matter when another wall ends at
    // such a vertex.
    for (const wall of walls) {
        const pts = wall.points;
        if (pts.length < 2) continue;
        for (let i = 0; i < pts.length; i++) {
            const p = pts[i];
            const key = `${p.x},${p.y}`;
            let entry = inc.get(key);
            if (!entry) {
                entry = { x: p.x, y: p.y, dirs: new Set() };
                inc.set(key, entry);
            }
            if (i + 1 < pts.length) {
                const d = dir(p, pts[i + 1]);
                if (d) entry.dirs.add(d);
            }
            if (i > 0) {
                const d = dir(p, pts[i - 1]);
                if (d) entry.dirs.add(d);
            }
        }
    }

    // Pass 2: when an incidence vertex lies on the interior of another
    // wall's segment (the segment passes through that vertex without
    // having it as a stored polyline point), add the segment's forward
    // and backward directions to the vertex. This catches T-junctions
    // where one wall's endpoint sits at the middle of another wall.
    for (const entry of inc.values()) {
        for (const wall of walls) {
            const pts = wall.points;
            for (let i = 0; i < pts.length - 1; i++) {
                const a = pts[i];
                const b = pts[i + 1];
                if (!pointOnSegmentInterior(entry.x, entry.y, a, b)) continue;
                const fwd = dir(a, b);
                const back = dir(b, a);
                if (fwd) entry.dirs.add(fwd);
                if (back) entry.dirs.add(back);
            }
        }
    }

    // Pass 3: segment-segment intersections strictly interior to BOTH
    // segments. This catches cross-junctions where two walls cross each
    // other without either having the crossing point as an endpoint
    // (e.g., a long vertical wall and a long horizontal wall that pass
    // through each other). Each intersection adds a new incidence
    // vertex carrying both segments' forward/backward directions.
    for (let i = 0; i < walls.length; i++) {
        const ptsI = walls[i].points;
        for (let segI = 0; segI < ptsI.length - 1; segI++) {
            const a = ptsI[segI];
            const b = ptsI[segI + 1];
            const dab = dir(a, b);
            const dba = dir(b, a);
            for (let j = i + 1; j < walls.length; j++) {
                const ptsJ = walls[j].points;
                for (let segJ = 0; segJ < ptsJ.length - 1; segJ++) {
                    const c = ptsJ[segJ];
                    const d = ptsJ[segJ + 1];
                    const ipt = segmentIntersectionStrict(a, b, c, d);
                    if (!ipt) continue;
                    const key = `${ipt.x},${ipt.y}`;
                    let entry = inc.get(key);
                    if (!entry) {
                        entry = { x: ipt.x, y: ipt.y, dirs: new Set() };
                        inc.set(key, entry);
                    }
                    if (dab) entry.dirs.add(dab);
                    if (dba) entry.dirs.add(dba);
                    const dcd = dir(c, d);
                    const ddc = dir(d, c);
                    if (dcd) entry.dirs.add(dcd);
                    if (ddc) entry.dirs.add(ddc);
                }
            }
        }
    }

    return inc;
}

/**
 * Strict intersection point of segment a→b and segment c→d, or null
 * if they don't cross strictly interior to both. "Strictly interior"
 * means the intersection point is not at any of the four endpoints.
 * (Endpoint coincidences are handled by passes 1 and 2.)
 * @param {Pt} a
 * @param {Pt} b
 * @param {Pt} c
 * @param {Pt} d
 * @returns {Pt|null}
 */
function segmentIntersectionStrict(a, b, c, d) {
    const rx = b.x - a.x;
    const ry = b.y - a.y;
    const sx = d.x - c.x;
    const sy = d.y - c.y;
    const denom = rx * sy - ry * sx;
    if (denom === 0) return null; // parallel or collinear
    const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / denom;
    const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / denom;
    // Strict interior on both segments: t and u in (0, 1).
    if (t <= 0 || t >= 1 || u <= 0 || u >= 1) return null;
    return { x: a.x + t * rx, y: a.y + t * ry };
}

/**
 * True when (px, py) lies strictly on the interior of segment a→b, at any
 * angle. Endpoints are excluded.
 * @param {number} px
 * @param {number} py
 * @param {Pt} a
 * @param {Pt} b
 */
function pointOnSegmentInterior(px, py, a, b) {
    if ((px === a.x && py === a.y) || (px === b.x && py === b.y)) return false;
    // Collinearity: 2D cross product is zero.
    const cross = (b.x - a.x) * (py - a.y) - (b.y - a.y) * (px - a.x);
    if (cross !== 0) return false;
    // Within bounds along whichever axes the segment spans.
    if (a.x !== b.x && (px < Math.min(a.x, b.x) || px > Math.max(a.x, b.x))) return false;
    if (a.y !== b.y && (py < Math.min(a.y, b.y) || py > Math.max(a.y, b.y))) return false;
    return true;
}

/**
 * Render a list of wall polylines together. Vertices shared by multiple
 * polylines (or interior bends within one) are decorated with the right
 * piece — Corner / Joint / Connector / Ending — based on the global
 * incidence of cardinal wall directions at each grid point.
 *
 * `doorEndpointKeys` is the set of "x,y" grid keys at which a door
 * terminates; corners at those points render with the rounded jamb
 * variant.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<{id:string,brushId:string,points:Pt[]}>} walls
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 * @param {Set<string>} [doorEndpointKeys]
 */
export function drawTileBrush(ctx, walls, brush, textures, cellPx, doorEndpointKeys) {
    // Walls with the same brush that share an endpoint vertex of
    // degree 2 (only those two walls touching it) render as a single
    // polyline, so corner mitering and connector composition work
    // across the join. Vertices of degree ≥ 3 (T/cross junctions)
    // remain on the splitting boundary — those are handled by the
    // existing vertex-tile pass for axis brushes.
    walls = mergeWallChains(walls);

    // Z-order: axis straights → diagonal bridges → connectors. The
    // connectors (axis vertex tiles + diagonal connector pieces) all
    // sit on top so their joiner content covers the seams where
    // straights/bridges butt against each other.

    // Crossing / T-junction grid points across ALL walls (a vertex where three
    // or four wall directions meet). A straight tile must not span one of these:
    // pass 3a draws the Joint/Connector piece there and it needs a seam beneath
    // it. So drawWallStraights breaks its tiles at these points — a 3x crossed
    // mid-span is rendered as a 1x/2x combination with the seam at the crossing.
    const inc = buildVertexIncidence(walls);
    const junctionKeys = new Set();
    for (const entry of inc.values()) {
        if (entry.dirs.size >= 3) junctionKeys.add(`${entry.x},${entry.y}`);
    }

    // Inset vertices: the nodes where pass 3a actually draws a Corner/Joint/
    // Connector piece. Pass 1 half-blocks the cells flanking these so the vertex
    // piece sits flush in its own cell (no overlap). Only built when the brush
    // ships a `straightHalf` piece — otherwise pass 1 keeps the full-length
    // overlap. Reusing pickVertexTile keeps the inset decision from ever drifting
    // from the "is a vertex tile drawn here" decision; it already returns null for
    // collinear pass-throughs, any diagonal incidence, and endings (size 1).
    const halfBlock = brush.pieces.straightHalf;
    /** @type {Set<string>|undefined} */
    let insetKeys;
    if (halfBlock && halfBlock.length > 0) {
        insetKeys = new Set();
        for (const entry of inc.values()) {
            if (entry.dirs.size < 2) continue;
            if (pickVertexTile(brush, entry.dirs, entry.x, entry.y, false)) {
                insetKeys.add(`${entry.x},${entry.y}`);
            }
        }
    }

    // Joins where an angled run meets another wall: every segment touching one is
    // clipped to its sector there (mitered corners, merged T/crossings) by
    // drawJoinedSegment in passes 1 and 2.
    const joins = buildAngledJoins(inc);

    // Pass 1: axis straights. Seam connectors are collected here but drawn last
    // (pass 4), so an overlay that bridges a half-block's joint with a vertex
    // piece lands on top of that piece instead of under it.
    /** @type {Array<{gx:number,gy:number,angle:number,joinClips?:JoinTouch[]}>} */
    const seams = [];
    for (const wall of walls) {
        drawWallStraights(
            ctx,
            wall,
            brush,
            textures,
            cellPx,
            junctionKeys,
            insetKeys,
            seams,
            joins,
        );
    }

    // Pass 2: diagonal bridges. Brushes without a connectorDiag piece
    // fall back to stretching axial straight tiles via
    // drawDiagonalStraightsFallback (kept brush-agnostic here — the
    // fallback handles its own connector behavior).
    const hasDiagPiece = !!(brush.pieces.connectorDiag && brush.pieces.connectorDiag.length > 0);
    for (const wall of walls) {
        forEachDiagonalSegment(wall, (a, b, d, segLen, i) => {
            drawJoinedSegment(ctx, joins, a, b, d, brush, textures, cellPx, seams, () => {
                if (hasDiagPiece) {
                    drawDiagonalBridge(ctx, a, b, d, segLen, i, brush, textures, cellPx, seams);
                } else {
                    drawDiagonalStraightsFallback(ctx, a, d, segLen, i, brush, textures, cellPx);
                }
            });
        });
        // Angled (non-45°) runs, drawn at their true angle alongside the bridges.
        forEachAngledSegment(wall, (a, b, d, i) => {
            drawJoinedSegment(ctx, joins, a, b, d, brush, textures, cellPx, seams, () =>
                drawAngledRun(ctx, a, b, d, i, brush, textures, cellPx, seams),
            );
        });
    }

    // Pass 3a: axis vertex tiles (corners, joints, connectors,
    // endings). Skipped at vertices that have a diagonal incidence —
    // the diagonal connector tiles handle those. Reuses `inc` from above.
    for (const entry of inc.values()) {
        const isDoorAdjacent = doorEndpointKeys
            ? doorEndpointKeys.has(`${entry.x},${entry.y}`)
            : false;
        const choice = pickVertexTile(brush, entry.dirs, entry.x, entry.y, isDoorAdjacent);
        if (!choice) continue;
        drawTileAt(ctx, entry.x, entry.y, choice.tile, brush, textures, cellPx, choice.angle);
    }

    // Pass 3b: diagonal elbow tiles (Connector_DIAG) — only at diagonal↔axis
    // junctions, not at free diagonal ends.
    if (hasDiagPiece) {
        for (const wall of walls) {
            forEachDiagonalSegment(wall, (a, b, d, _segLen, i, prevD, nextD) => {
                drawDiagonalConnectors(ctx, a, b, d, i, brush, textures, cellPx, prevD, nextD);
            });
        }
    }

    // Pass 4: straight-seam connectors, drawn on top of the straights, half-blocks,
    // and vertex pieces — bridging each butt joint (pack seams, a half-block's
    // joints with the run and the vertex piece, and diagonal-bridge seams).
    const seamPieces = brush.pieces.connectorStraight;
    for (const s of seams) {
        if (s.joinClips) {
            ctx.save();
            clipToJoins(ctx, s.joinClips, cellPx);
            drawSeamConnector(ctx, s.gx, s.gy, seamPieces, brush, textures, cellPx, s.angle);
            ctx.restore();
        } else {
            drawSeamConnector(ctx, s.gx, s.gy, seamPieces, brush, textures, cellPx, s.angle);
        }
    }

    // Pass 5: diagonal cross piece where two diagonals cross (all four diagonal
    // directions incident) — a Joint_A rotated 45° covering the X seam. Drawn LAST,
    // after the seam connectors, so a diagonal-bridge seam that lands on the
    // crossing point doesn't cover it.
    const diagCross = brush.pieces.connectorDiagCross;
    if (diagCross && diagCross.length > 0) {
        for (const entry of inc.values()) {
            const { dirs } = entry;
            if (dirs.has('ne') && dirs.has('nw') && dirs.has('se') && dirs.has('sw')) {
                const tile = pickVariant(diagCross, entry.x, entry.y, 'diag-cross');
                if (tile)
                    drawTileAt(ctx, entry.x, entry.y, tile, brush, textures, cellPx, Math.PI / 4);
            }
        }
    }
}

/**
 * Draw a single tile centered at the given grid coords (in cells), rotated
 * by `angle` radians. The tile's pixel size is derived from its cells dims
 * × cellPx, scaled from the manifest's authoring resolution
 * (200 px / cell) so authoring at 200 dpi just works.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} cellX  world cell x (centered horizontally)
 * @param {number} cellY  world cell y (centered vertically)
 * @param {Tile} tile
 * @param {TileBrush} brush
 * @param {Map<string, HTMLImageElement>} textures
 * @param {number} cellPx
 * @param {number} angle radians
 * @param {number} [axisStretch=1]
 *   Multiplier on the tile's along-wall pixel width — only the along-axis
 *   dimension is stretched, the perpendicular thickness stays at cellsH × cellPx.
 *   Diagonal bridges pass √2 so a 1-cell tile spans one diagonal vertex-step.
 * @param {boolean} [flipH=false]
 * @param {number} [trimEnds=0] see drawRotated
 */
function drawTileAt(
    ctx,
    cellX,
    cellY,
    tile,
    brush,
    textures,
    cellPx,
    angle,
    axisStretch = 1,
    flipH = false,
    trimEnds = 0,
) {
    const url = tileUrl(brush, tile);
    const img = textures.get(url);
    if (!img) return;
    const w = tile.cellsW * cellPx * axisStretch;
    const h = tile.cellsH * cellPx;
    // World coords in pixels: cell coords × cellPx.
    drawRotated(ctx, img, cellX * cellPx, cellY * cellPx, w, h, angle, flipH, trimEnds);
}

/**
 * Collect every {brush, tile} URL referenced by the brush so the editor
 * can preload them. Returns an array of URLs.
 * @param {TileBrush} brush
 */
export function collectBrushTextureUrls(brush) {
    /** @type {string[]} */
    const out = [];
    for (const role of /** @type {const} */ ([
        'straight',
        'straightHalf',
        'corner',
        'ending',
        'joint',
        'connector',
        'connectorStraight',
        'connectorDiag',
        'connectorDiagCross',
    ])) {
        const tiles = brush.pieces[role];
        if (!tiles) continue;
        for (const t of tiles) out.push(tileUrl(brush, t));
    }
    return out;
}

// Expose a constant for callers that want the authoring resolution.
export const _AUTHORING_PX_PER_CELL = TILE_TEXTURE_PIXELS_PER_CELL;
